import { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers';
import { logger } from '@/utils/logger';

/**
 * Utility functions for resolving language across different environments and contexts
 */

// Define valid languages
export const VALID_LANGUAGES = ['en-us', 'zh-cn', 'zh-hk'] as const;
export type ValidLanguage = (typeof VALID_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: ValidLanguage = 'en-us';

interface LanguageResolverOptions {
  pathLanguage?: string | undefined;
  headers?: ReadonlyHeaders | Headers | undefined;
  cookies?: Record<string, string> | undefined;
  localStorage?: Storage | undefined;
}

/**
 * Get the current language from multiple sources with fallbacks
 * Order of precedence:
 * 1. URL path parameter
 * 2. Header from Netlify (x-netlify-language or x-current-locale)
 * 3. Browser cookie
 * 4. LocalStorage
 * 5. Default language (en-us)
 */
export const resolveLanguage = ({
  pathLanguage,
  headers,
  cookies,
  localStorage,
}: LanguageResolverOptions = {}): ValidLanguage => {
  // Check URL path parameter first (highest priority)
  if (pathLanguage && VALID_LANGUAGES.includes(pathLanguage as ValidLanguage)) {
    logger.log('Language resolved from URL path:', pathLanguage);
    return pathLanguage as ValidLanguage;
  }

  // Check headers set by Netlify
  if (headers) {
    // Try the custom Netlify header first
    const netlifyLang = headers.get('x-netlify-language');
    if (netlifyLang && VALID_LANGUAGES.includes(netlifyLang as ValidLanguage)) {
      logger.log('Language resolved from x-netlify-language header:', netlifyLang);
      return netlifyLang as ValidLanguage;
    }

    // Try the middleware header
    const currentLocale = headers.get('x-current-locale');
    if (currentLocale && VALID_LANGUAGES.includes(currentLocale as ValidLanguage)) {
      logger.log('Language resolved from x-current-locale header:', currentLocale);
      return currentLocale as ValidLanguage;
    }
  }

  // Check cookie
  if (cookies) {
    const cookieLang = cookies['NEXT_LOCALE'];
    if (cookieLang && VALID_LANGUAGES.includes(cookieLang as ValidLanguage)) {
      logger.log('Language resolved from cookie:', cookieLang);
      return cookieLang as ValidLanguage;
    }
  }

  // Check localStorage (client-side only)
  if (localStorage) {
    try {
      const storedLang = localStorage.getItem('preferredLanguage');
      if (storedLang && VALID_LANGUAGES.includes(storedLang as ValidLanguage)) {
        logger.log('Language resolved from localStorage:', storedLang);
        return storedLang as ValidLanguage;
      }
    } catch (e) {
      logger.warn('Error accessing localStorage:', e);
    }
  }

  // Default fallback
  logger.log('Using default language:', DEFAULT_LANGUAGE);
  return DEFAULT_LANGUAGE;
};
